<template>
  <div v-if="isATwilioChannel" class="settings--content">
    <settings-section
      :title="$t('INBOX_MGMT.ADD.TWILIO.API_CALLBACK.TITLE')"
      :sub-title="$t('INBOX_MGMT.ADD.TWILIO.API_CALLBACK.SUBTITLE')"
    >
      <woot-code :script="inbox.callback_webhook_url" lang="html" />
    </settings-section>
  </div>
  <div v-else-if="isALineChannel" class="settings--content">
    <settings-section
      :title="$t('INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.TITLE')"
      :sub-title="$t('INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.SUBTITLE')"
    >
      <woot-code :script="inbox.callback_webhook_url" lang="html" />
    </settings-section>
  </div>
  <div v-else-if="isAWebWidgetInbox">
    <div class="settings--content">
      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.MESSENGER_HEADING')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.MESSENGER_SUB_HEAD')"
      >
        <woot-code
          :script="inbox.web_widget_script"
          lang="html"
          :codepen-title="`${inbox.name} - Chatwoot Widget Test`"
          :enable-code-pen="true"
        />
      </settings-section>

      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.WEB_WIDGET_PAGE_HEADING')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.WEB_WIDGET_PAGE_SUB_HEAD')"
      >
        <woot-code :script="inbox.web_widget_page_url" />
      </settings-section>

      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.WEB_WIDGET_HANDOFF_HEADING')"
        :sub-title="
          $t('INBOX_MGMT.SETTINGS_POPUP.WEB_WIDGET_HANDOFF_SUB_HEAD', {
            tag: '{% webchat_link %}',
          })
        "
      >
        <woot-code :script="webchatLinkTag" />
        <div class="enter-to-send--checkbox">
          <input
            id="handoffDefault"
            v-model="handoffDefault"
            type="checkbox"
            @change="updateHandoffDefault"
          />
          <label for="handoffDefault">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WEB_WIDGET_HANDOFF_DEFAULT') }}
          </label>
        </div>
      </settings-section>

      <settings-section
        :title="
          $t('INBOX_MGMT.SETTINGS_POPUP.WEB_WIDGET_HANDOFF_MESSAGE_HEADING')
        "
        :sub-title="
          $t('INBOX_MGMT.SETTINGS_POPUP.WEB_WIDGET_HANDOFF_MESSAGE_SUB_HEAD', {
            variable: '{{contact.name}}',
          })
        "
      >
        <label>
          <select v-model="handoffMessageType">
            <option
              v-for="option in handoffMessageTypes"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>
        <label
          v-if="handoffMessageType === 'custom'"
          :class="{ error: $v.handoffMessage.$error }"
        >
          <textarea
            v-model="handoffMessage"
            rows="5"
            :placeholder="
              $t(
                'INBOX_MGMT.SETTINGS_POPUP.WEB_WIDGET_HANDOFF_MESSAGE_PLACEHOLDER',
                {
                  variable: '{{contact.name}}',
                }
              )
            "
            @blur="$v.handoffMessage.$touch"
          />
          <span v-if="$v.handoffMessage.$error" class="message">
            {{
              $t('INBOX_MGMT.SETTINGS_POPUP.WEB_WIDGET_HANDOFF_MESSAGE_ERROR')
            }}
          </span>
        </label>
        <woot-button
          :disabled="$v.handoffMessage.$invalid"
          @click="updateHandoffMessage"
        >
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
        </woot-button>
      </settings-section>

      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_VERIFICATION')"
      >
        <woot-code :script="inbox.hmac_token" />
        <template #subTitle>
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.HMAC_DESCRIPTION') }}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://www.chatwoot.com/docs/product/channels/live-chat/sdk/identity-validation/"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.HMAC_LINK_TO_DOCS') }}
          </a>
        </template>
      </settings-section>
      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_VERIFICATION')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_DESCRIPTION')"
      >
        <div class="enter-to-send--checkbox">
          <input
            id="hmacMandatory"
            v-model="hmacMandatory"
            type="checkbox"
            @change="handleHmacFlag"
          />
          <label for="hmacMandatory">
            {{ $t('INBOX_MGMT.EDIT.ENABLE_HMAC.LABEL') }}
          </label>
        </div>
      </settings-section>
    </div>
  </div>
  <div v-else-if="isAPIInbox" class="settings--content">
    <settings-section
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_IDENTIFIER')"
      :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_IDENTIFIER_SUB_TEXT')"
    >
      <woot-code :script="inbox.inbox_identifier" />
    </settings-section>

    <settings-section
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_VERIFICATION')"
      :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_DESCRIPTION')"
    >
      <woot-code :script="inbox.hmac_token" />
    </settings-section>
    <settings-section
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_VERIFICATION')"
      :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_DESCRIPTION')"
    >
      <div class="enter-to-send--checkbox">
        <input
          id="hmacMandatory"
          v-model="hmacMandatory"
          type="checkbox"
          @change="handleHmacFlag"
        />
        <label for="hmacMandatory">
          {{ $t('INBOX_MGMT.EDIT.ENABLE_HMAC.LABEL') }}
        </label>
      </div>
    </settings-section>
  </div>
  <div v-else-if="isAnEmailChannel">
    <div class="settings--content">
      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_SUB_TEXT')"
      >
        <woot-code :script="inbox.forward_to_email" />
      </settings-section>
    </div>
    <imap-settings :inbox="inbox" />
    <smtp-settings v-if="inbox.imap_enabled" :inbox="inbox" />
    <microsoft-reauthorize
      v-if="inbox.microsoft_reauthorization"
      :inbox="inbox"
    />
  </div>
  <div v-else-if="is360DialogWhatsAppChannel">
    <div v-if="inbox.provider_config" class="settings--content">
      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_SUBHEADER')"
      >
        <woot-code :script="inbox.provider_config.webhook_verify_token" />
      </settings-section>
      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_SUBHEADER')"
      >
        <woot-code :script="inbox.provider_config.api_key" />
      </settings-section>
      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_TITLE')"
        :sub-title="
          $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_SUBHEADER')
        "
      >
        <div class="whatsapp-settings--content">
          <woot-input
            v-model.trim="whatsAppInboxAPIKey"
            type="text"
            class="input"
            :placeholder="
              $t(
                'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_PLACEHOLDER'
              )
            "
          />
          <woot-button
            :disabled="$v.whatsAppInboxAPIKey.$invalid"
            @click="updateWhatsAppInboxAPIKey"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
      <settings-section
        title="Migrar para o Cloud API"
        sub-title="Este botão faz a troca do cadastro interno do número do On-Premisse para o Cloud API."
      >
        <div class="whatsapp-settings--content">
          <woot-button
            :disabled="inbox.provider != 'default'"
            @click="updateWhatsAppInboxProvider"
          >
            Migrar para o Cloud API
          </woot-button>
        </div>
      </settings-section>
      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_TITLE_AUDIO')"
        :sub-title="
          $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_SUBHEADER_AUDIO')
        "
      >
        <div class="whatsapp-settings--content">
          <label class="input-container input">
            <select v-model="audioWhatsAppInbox" style="margin-bottom: 0;">
              <option
                v-for="option in getwhatsAppAudios"
                :key="option.key"
                :value="option.key"
              >
                {{ option.text }}
              </option>
            </select>
          </label>
          <woot-button
            :disabled="$v.audioWhatsAppInbox.$invalid"
            @click="updateAudioWhatsAppInbox"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
      <whatsapp-coex-settings :inbox="inbox" />
    </div>
  </div>

  <div v-else-if="isVollChannel">
    <div class="settings--content">
      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.VOLL_SECTION_DOMAIN_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.VOLL_SECTION_DOMAIN_SUBHEADER')"
      >
        <woot-code :script="inbox.domain" />
      </settings-section>
      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.VOLL_SECTION_APIKEY_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.VOLL_SECTION_APIKEY_SUBHEADER')"
      >
        <woot-code :script="inbox.api_key" />
      </settings-section>
      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.VOLL_SECTION_APIKEY_UPDATE_TITLE')"
        :sub-title="
          $t('INBOX_MGMT.SETTINGS_POPUP.VOLL_SECTION_APIKEY_UPDATE_SUBHEADER')
        "
      >
        <div class="whatsapp-settings--content">
          <woot-input
            v-model.trim="vollInboxAPIKey"
            type="text"
            class="input"
            :placeholder="
              $t(
                'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_PLACEHOLDER'
              )
            "
          />
          <woot-button
            :disabled="$v.vollInboxAPIKey.$invalid"
            @click="updateVollInboxAPIKey"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
      <settings-section
        title="Número"
        sub-title="Filtro aplicado por Número"
      >
        <div class="whatsapp-settings--content">
          <woot-input
            v-model.trim="vollNumber"
            type="text"
            class="input"
            placeholder="+55DDD900000000"
          />
          <woot-button
            :disabled="$v.vollNumber.$invalid"
            @click="updateVollNumber"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
      <settings-section
        title="Agentes"
        sub-title="Filtro aplicado por agentes"
      >
        <div class="whatsapp-settings--content">
          <multiselect
            v-model="vollAgents"
            selectedLabel="Selecionado"
            placeholder="Selecionar agentes"
            label="name"
            track-by="id"
            deselect-label=""
            :max-height="160"
            :allow-empty="true"
            :option-height="104"
            :multiple="true"
            :options="vollAgentsOptions"
            :disabled="!showAgents"
            :searchable="true"
          >
            <span slot="noOptions">Lista vazia</span>
          </multiselect>
          <woot-button
            style="align-self: baseline;margin-left: 0.8rem;"
            @click="updateVollAgents"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
      <settings-section
        title="Campanhas"
        sub-title="Filtro aplicado por campanhas"
      >
        <div class="whatsapp-settings--content">
          <multiselect
            v-model="vollCampaigns"
            selectedLabel="Selecionado"
            placeholder="Selecionar campanhas"
            label="name"
            track-by="id"
            deselect-label=""
            :max-height="160"
            :allow-empty="true"
            :option-height="104"
            :multiple="true"
            :options="vollCampaingsOptions"
            :disabled="!showCampaigns"
            :searchable="true"
          >
            <span slot="noOptions">Lista vazia</span>
          </multiselect>
          <woot-button
            style="align-self: baseline;margin-left: 0.8rem;"
            @click="updateVollCampaigns"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
      <settings-section
        title="Tabulações"
        sub-title="Filtro aplicado por tabulações"
      >
        <div class="whatsapp-settings--content">
          <multiselect
            v-model="vollTabulations"
            selectedLabel="Selecionado"
            placeholder="Selecionar tabulações"
            label="name"
            track-by="id"
            deselect-label=""
            :max-height="160"
            :allow-empty="true"
            :option-height="104"
            :multiple="true"
            :options="vollTabulationsOptions"
            :disabled="!showTabulation"
            :searchable="true"
          >
            <span slot="noOptions">Lista vazia</span>
          </multiselect>
          <woot-button
            style="align-self: baseline;margin-left: 0.8rem;"
            @click="updateVollTabulations"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
      <settings-section
        title="Mínimo de mensagens"
        sub-title="Filtro aplicado por quantidade mínima de mensanges na conversa"
      >
        <div class="whatsapp-settings--content">
          <woot-input
            v-model.trim="vollMinMessages"
            type="text"
            class="input"
          />
          <woot-button
            :disabled="$v.vollMinMessages.$invalid"
            @click="updateVollMinMessages"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
    </div>
  </div>

  <div v-else-if="isWetalkieChannel">
    <div class="settings--content">
      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.WETALKIE_SECTION_DOMAIN_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.WETALKIE_SECTION_DOMAIN_SUBHEADER')"
      >
        <woot-code :script="inbox.domain" />
      </settings-section>
      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.WETALKIE_SECTION_USERNAME_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.WETALKIE_SECTION_USERNAME_SUBHEADER')"
      >
        <woot-code :script="inbox.username" />
      </settings-section>

      <!--    ////////////////////////////////////////////////    -->

      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.WETALKIE_SECTION_DOMAIN_UPDATE_TITLE')"
        :sub-title="
          $t('INBOX_MGMT.SETTINGS_POPUP.WETALKIE_SECTION_DOMAIN_UPDATE_SUBHEADER')
        "
      >
        <div class="whatsapp-settings--content">
          <woot-input
            v-model.trim="wetalkieDomain"
            type="text"
            class="input"
            placeholder="Digite o novo domínio aqui"
          />
          <woot-button
            :disabled="$v.wetalkieDomain.$invalid"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.WETALKIE_SECTION_USERNAME_UPDATE_TITLE')"
        :sub-title="
          $t('INBOX_MGMT.SETTINGS_POPUP.WETALKIE_SECTION_USERNAME_UPDATE_SUBHEADER')
        "
      >
        <div class="whatsapp-settings--content">
          <woot-input
            v-model.trim="wetalkieUsername"
            type="text"
            class="input"
            placeholder="Digite o novo username aqui"
          />
          <woot-button
            :disabled="$v.wetalkieUsername.$invalid"
            @click="updateWetalkieUsername"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.WETALKIE_SECTION_PASSWORD_UPDATE_TITLE')"
        :sub-title="
          $t('INBOX_MGMT.SETTINGS_POPUP.WETALKIE_SECTION_PASSWORD_UPDATE_SUBHEADER')
        "
      >
        <div class="whatsapp-settings--content">
          <woot-input
            v-model.trim="wetalkiePassword"
            type="text"
            class="input"
            placeholder="Digite a nova senha aqui"
          />
          <woot-button
            :disabled="$v.wetalkiePassword.$invalid"
            @click="updateWetalkiePassword"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
      <settings-section
        title="Tabulações"
        sub-title="Filtro aplicado por tabulações"
      >
        <div class="whatsapp-settings--content">
          <multiselect
            v-model="wetalkieTabulations"
            selectedLabel="Selecionado"
            placeholder="Selecionar tabulações"
            label="name"
            track-by="id"
            deselect-label=""
            :loading="wetalkieLoadingTabulations"
            :max-height="160"
            :allow-empty="true"
            :option-height="104"
            :multiple="true"
            :options="wetalkieTabulationsOptions"
            :disabled="!showTabulation"
            :searchable="true"
          >
            <span slot="noOptions">Lista vazia</span>
          </multiselect>
          <woot-button
            style="align-self: baseline;margin-left: 0.8rem;"
            @click="updateWetalkieData"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
      <settings-section
        title="Usuários"
        sub-title="Filtro aplicado por usuários"
      >
        <div class="whatsapp-settings--content">
          <multiselect
            v-model="wetalkieUsers"
            selectedLabel="Selecionado"
            placeholder="Selecionar usuários"
            label="personName"
            track-by="id"
            deselect-label=""
            :loading="wetalkieLoadingUsers"
            :max-height="160"
            :allow-empty="true"
            :option-height="104"
            :multiple="true"
            :options="wetalkieUserOptions"
            :disabled="!showIgnoredUsers"
            :searchable="true"
          >
            <span slot="noOptions">Lista vazia</span>
          </multiselect>
          <woot-button
            style="align-self: baseline;margin-left: 0.8rem;"
            @click="updateWetalkieData"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
      <settings-section
        title="Usuários ignorados"
        sub-title="Filtro aplicado por usuários ignorados"
      >
        <div class="whatsapp-settings--content">
          <multiselect
            v-model="wetalkieIgnoredUsers"
            selectedLabel="Selecionado"
            placeholder="Selecionar usuários"
            label="personName"
            track-by="id"
            deselect-label=""
            :loading="wetalkieLoadingUsers"
            :max-height="160"
            :allow-empty="true"
            :option-height="104"
            :multiple="true"
            :options="wetalkieUserOptions"
            :disabled="!showIgnoredUsers"
            :searchable="true"
          >
            <span slot="noOptions">Lista vazia</span>
          </multiselect>
          <woot-button
            style="align-self: baseline;margin-left: 0.8rem;"
            @click="updateWetalkieData"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
      <settings-section
        title="Mínimo de mensagens"
        sub-title="Filtro aplicado por quantidade mínima de mensanges na conversa"
      >
        <div class="whatsapp-settings--content">
          <woot-input
            v-model.trim="wetalkieMinMessages"
            type="text"
            class="input"
          />
          <woot-button
            :disabled="$v.wetalkieMinMessages.$invalid"
            @click="updateWetalkieData"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
      <settings-section
        title="Mínimo de mensagens do cliente"
        sub-title="Filtro aplicado por quantidade mínima de mensanges na conversa"
      >
        <div class="whatsapp-settings--content">
          <woot-input
            v-model.trim="wetalkieCustomerMinMessages"
            type="text"
            class="input"
          />
          <woot-button
            :disabled="$v.wetalkieCustomerMinMessages.$invalid"
            @click="updateWetalkieData"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
      <settings-section
        title="Mínimo de mensagens do operador"
        sub-title="Filtro aplicado por quantidade mínima de mensanges na conversa"
      >
        <div class="whatsapp-settings--content">
          <woot-input
            v-model.trim="wetalkieOperatorMinMessages"
            type="text"
            class="input"
          />
          <woot-button
            :disabled="$v.wetalkieOperatorMinMessages.$invalid"
            @click="updateWetalkieData"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
      <settings-section
        title="Contabilizar mensagens do bot"
        sub-title="Se filtro aplicado de quantidade mínima de mensagens na conversa deve considerar as mensagens do bot"
      >
        <div class="whatsapp-settings--content">
          <woot-switch v-model="wetalkieCountBotMessages" />
          <woot-button @click="updateWetalkieData">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
      <settings-section
        title="Execução retroativa"
        sub-title="Busca as conversas da Wetalkie no período informado e as processa retroativamente em segundo plano."
      >
        <div class="whatsapp-settings--content" style="flex-wrap: wrap; gap: 0.8rem;">
          <woot-input
            v-model="wetalkieFetchBeginDate"
            type="date"
            class="input"
            label="Data inicial"
          />
          <woot-input
            v-model="wetalkieFetchEndDate"
            type="date"
            class="input"
            label="Data final"
          />
        </div>
        <div class="whatsapp-settings--content">
          <div class="enter-to-send--checkbox">
            <input
              id="wetalkieForce"
              v-model="wetalkieFetchForce"
              type="checkbox"
            />
            <label for="wetalkieForce">
              Forçar reprocessamento de conversas já existentes
            </label>
          </div>
          <woot-button
            :is-loading="wetalkieFetching"
            :disabled="
              wetalkieFetching ||
              !wetalkieFetchBeginDate ||
              !wetalkieFetchEndDate
            "
            @click="runWetalkieFetch"
          >
            Executar
          </woot-button>
        </div>
      </settings-section>
      <settings-section
        title="Ingestão de conversas"
        sub-title="Bloqueia manualmente a busca automática de conversas da Wetalkie até a data/hora informada. Use para liberar uma inbox bloqueada indevidamente ou pausar a ingestão sob demanda."
      >
        <p
          v-if="isIngestionPaused"
          class="ingestion-status ingestion-status--paused"
        >
          Ingestão bloqueada até <strong>{{ ingestionPausedLabel }}</strong>.
        </p>
        <p v-else class="ingestion-status ingestion-status--active">
          Ingestão ativa.
        </p>
        <template v-if="!isIngestionPaused">
          <div class="whatsapp-settings--content">
            <woot-input
              v-model="ingestionPauseUntilInput"
              type="datetime-local"
              class="input"
              label="Bloquear até"
            />
          </div>
          <div class="whatsapp-settings--content">
            <woot-button
              color-scheme="alert"
              :is-loading="ingestionPauseLoading"
              :disabled="ingestionPauseLoading || !ingestionPauseUntilInput"
              @click="blockIngestion"
            >
              Bloquear ingestão
            </woot-button>
          </div>
        </template>
        <div v-else class="whatsapp-settings--content">
          <woot-button
            color-scheme="success"
            :is-loading="ingestionPauseLoading"
            :disabled="ingestionPauseLoading"
            @click="releaseIngestion"
          >
            Liberar ingestão
          </woot-button>
        </div>
      </settings-section>
    </div>
  </div>

  <div v-else-if="isZendeskChannel">
    <div class="settings--content">
      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.ZENDESK_SECTION_SUBDOMAIN_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.ZENDESK_SECTION_SUBDOMAIN_SUBHEADER')"
      >
        <woot-code :script="inbox.subdomain" />
      </settings-section>

      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.ZENDESK_SECTION_EMAIL_UPDATE_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.ZENDESK_SECTION_EMAIL_SUBHEADER')"
      >
        <woot-code :script="inbox.email" />
      </settings-section>

      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.ZENDESK_SECTION_API_TOKEN_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.ZENDESK_SECTION_API_TOKEN_SUBHEADER')"
      >
        <woot-code :script="inbox.api_token" />
      </settings-section>

      <!--    ////////////////////////////////////////////////    -->

      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.ZENDESK_SECTION_SUBDOMAIN_UPDATE_TITLE')"
        :sub-title="
          $t('INBOX_MGMT.SETTINGS_POPUP.ZENDESK_SECTION_SUBDOMAIN_UPDATE_SUBHEADER')
        "
      >
        <div class="whatsapp-settings--content">
          <woot-input
            v-model.trim="zendeskSubdomain"
            type="text"
            class="input"
            placeholder="Digite o novo domínio aqui"
          />
          <woot-button
            :disabled="$v.zendeskSubdomain.$invalid"
            @click="updateZendeskSubdomain"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.ZENDESK_SECTION_EMAIL_UPDATE_TITLE')"
        :sub-title="
          $t('INBOX_MGMT.SETTINGS_POPUP.ZENDESK_SECTION_EMAIL_UPDATE_SUBHEADER')
        "
      >
        <div class="whatsapp-settings--content">
          <woot-input
            v-model.trim="zendeskEmail"
            type="text"
            class="input"
            placeholder="Digite o novo email aqui"
          />
          <woot-button
            :disabled="$v.zendeskEmail.$invalid"
            @click="updateZendeskEmail"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.ZENDESK_SECTION_API_TOKEN_UPDATE_TITLE')"
        :sub-title="
          $t('INBOX_MGMT.SETTINGS_POPUP.ZENDESK_SECTION_API_TOKEN_UPDATE_SUBHEADER')
        "
      >
        <div class="whatsapp-settings--content">
          <woot-input
            v-model.trim="zendeskApiToken"
            type="text"
            class="input"
            placeholder="Digite o nova api token aqui"
          />
          <woot-button
            :disabled="$v.zendeskApiToken.$invalid"
            @click="updateZendeskApiToken"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
      <settings-section
        title="Canais"
        sub-title="Filtro aplicado por Canais"
      >
        <div class="whatsapp-settings--content">
          <multiselect
            v-model="zendeskChannels"
            selectedLabel="Selecionado"
            placeholder="Selecionar canais"
            label="name"
            track-by="id"
            deselect-label=""
            :max-height="160"
            :allow-empty="true"
            :option-height="104"
            :multiple="true"
            :options="zendeskChannelsOptions"
            :searchable="true"
          >
            <span slot="noOptions">Lista vazia</span>
          </multiselect>
          <woot-button
            style="align-self: baseline;margin-left: 0.8rem;"
            @click="updateZendeskData"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
      <settings-section
        title="Grupos"
        sub-title="Filtro aplicado por grupos"
      >
        <div class="whatsapp-settings--content">
          <multiselect
            v-model="zendeskGroups"
            selectedLabel="Selecionado"
            placeholder="Selecionar grupos"
            label="name"
            track-by="id"
            deselect-label=""
            :loading="zendeskLoadingGroups"
            :max-height="160"
            :allow-empty="true"
            :option-height="104"
            :multiple="true"
            :options="zendeskGroupsOptions"
            :disabled="!showGroups"
            :searchable="true"
          >
            <span slot="noOptions">Lista vazia</span>
          </multiselect>
          <woot-button
            style="align-self: baseline;margin-left: 0.8rem;"
            @click="updateZendeskData"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
      <settings-section
        title="Tags Excluídas"
        sub-title="Considerar ignoras atendimentos com essas tags. Deixe vazio caso queira armazenar todas."
      >
        <div class="whatsapp-settings--content">
          <multiselect
            v-model="zendeskTags"
            selectedLabel="Selecionado"
            placeholder="Selecionar tags"
            label="name"
            track-by="name"
            deselect-label=""
            :loading="zendeskLoadingTags"
            :max-height="160"
            :allow-empty="true"
            :option-height="104"
            :multiple="true"
            :options="zendeskTagsOptions"
            :disabled="!showTags"
            :searchable="true"
          >
            <span slot="noOptions">Lista vazia</span>
          </multiselect>
          <woot-button
            style="align-self: baseline;margin-left: 0.8rem;"
            @click="updateZendeskData"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
      <settings-section
        title="Usuários"
        sub-title="Filtro aplicado por usuários"
      >
        <div class="whatsapp-settings--content">
          <multiselect
            v-model="zendeskUsers"
            selectedLabel="Selecionado"
            placeholder="Selecionar usuários"
            label="name"
            track-by="id"
            deselect-label=""
            :loading="zendeskLoadingUsers"
            :max-height="160"
            :allow-empty="true"
            :option-height="104"
            :multiple="true"
            :options="zendeskUsersOptions"
            :disabled="!showUsers"
            :searchable="true"
          >
            <span slot="noOptions">Lista vazia</span>
          </multiselect>
          <woot-button
            style="align-self: baseline;margin-left: 0.8rem;"
            @click="updateZendeskData"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
            <settings-section
        title="Usuários Excluídos"
        sub-title="Filtro aplicado para desconsiderar usuários"
      >
        <div class="whatsapp-settings--content">
          <multiselect
            v-model="zendeskExcludedUsers"
            selectedLabel="Selecionado"
            placeholder="Selecionar usuários"
            label="name"
            track-by="id"
            deselect-label=""
            :loading="zendeskLoadingUsers"
            :max-height="160"
            :allow-empty="true"
            :option-height="104"
            :multiple="true"
            :options="zendeskUsersOptions"
            :disabled="!showUsers"
            :searchable="true"
          >
            <span slot="noOptions">Lista vazia</span>
          </multiselect>
          <woot-button
            style="align-self: baseline;margin-left: 0.8rem;"
            @click="updateZendeskData"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
      <settings-section
        title="Mínimo de mensagens"
        sub-title="Filtro aplicado por quantidade mínima de mensanges na conversa"
      >
        <div class="whatsapp-settings--content">
          <woot-input
            v-model.trim="zendeskMinMessages"
            type="text"
            class="input"
          />
          <woot-button
            :disabled="$v.zendeskMinMessages.$invalid"
            @click="updateZendeskData"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
      <settings-section
        title="Contabilizar mensagens do bot"
        sub-title="Se filtro aplicado de quantidade mínima de mensagens na conversa deve considerar as mensagens do bot"
      >
        <div class="whatsapp-settings--content">
          <woot-switch v-model="zendeskCountBotMessages" />
          <woot-button @click="updateWetalkieData">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
      <settings-section
        title="Mínimo de mensagens do Cliente"
        sub-title="Filtro aplicado por quantidade mínima de mensanges do cliente na conversa"
      >
        <div class="whatsapp-settings--content">
          <woot-input
            v-model.trim="zendeskCustomerMinMessages"
            type="text"
            class="input"
          />
          <woot-button
            :disabled="$v.zendeskCustomerMinMessages.$invalid"
            @click="updateZendeskData"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
      <settings-section
        title="Mínimo de mensagens do Atendente"
        sub-title="Filtro aplicado por quantidade mínima de mensanges do atendente na conversa"
      >
        <div class="whatsapp-settings--content">
          <woot-input
            v-model.trim="zendeskOperatorMinMessages"
            type="text"
            class="input"
          />
          <woot-button
            :disabled="$v.zendeskOperatorMinMessages.$invalid"
            @click="updateZendeskData"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
      <settings-section
        title="Execução retroativa"
        sub-title="Busca as conversas do Zendesk no período informado e as processa retroativamente em segundo plano."
      >
        <div class="whatsapp-settings--content" style="flex-wrap: wrap; gap: 0.8rem;">
          <woot-input
            v-model="zendeskFetchBeginDate"
            type="date"
            class="input"
            label="Data inicial"
          />
          <woot-input
            v-model="zendeskFetchEndDate"
            type="date"
            class="input"
            label="Data final"
          />
        </div>
        <div class="whatsapp-settings--content">
          <div class="enter-to-send--checkbox">
            <input
              id="zendeskForce"
              v-model="zendeskFetchForce"
              type="checkbox"
            />
            <label for="zendeskForce">
              Forçar reprocessamento de conversas já existentes
            </label>
          </div>
          <woot-button
            :is-loading="zendeskFetching"
            :disabled="
              zendeskFetching ||
              !zendeskFetchBeginDate ||
              !zendeskFetchEndDate
            "
            @click="runZendeskFetch"
          >
            Executar
          </woot-button>
        </div>
      </settings-section>
    </div>
  </div>

  <div v-else-if="isInfobipChannel">
    <div class="settings--content">
      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_CHANNEL_TYPE_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_CHANNEL_TYPE_SUBHEADER')"
      >
        <woot-code :script="infobipChannelTypeLabel" />
      </settings-section>

      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_BASE_URL_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_BASE_URL_SUBHEADER')"
      >
        <woot-code :script="inbox.base_url" />
      </settings-section>

      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_BASE_URL_UPDATE_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_BASE_URL_UPDATE_SUBHEADER')"
      >
        <div class="whatsapp-settings--content">
          <woot-input v-model.trim="infobipBaseUrl" type="text" class="input" placeholder="https://api.infobip.com" />
          <woot-button :disabled="$v.infobipBaseUrl.$invalid" @click="updateInfobipCredential('base_url', infobipBaseUrl)">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>

      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_API_KEY_UPDATE_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_API_KEY_UPDATE_SUBHEADER')"
      >
        <div class="whatsapp-settings--content">
          <woot-input v-model.trim="infobipApiKey" type="text" class="input" placeholder="API Key" />
          <woot-button :disabled="$v.infobipApiKey.$invalid" @click="updateInfobipCredential('api_key', infobipApiKey)">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>

      <settings-section
        v-if="inbox.infobip_channel_type === 'whatsapp'"
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_PHONE_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_PHONE_SUBHEADER')"
      >
        <div class="whatsapp-settings--content">
          <woot-input
            v-model.trim="infobipPhoneNumber"
            type="text"
            class="input"
            :placeholder="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_PHONE_PLACEHOLDER')"
          />
          <woot-button style="align-self: baseline; margin-left: 0.8rem;" @click="updateInfobipData">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>

      <settings-section
        v-if="inbox.infobip_channel_type === 'live_chat'"
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_WIDGET_ID_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_WIDGET_ID_SUBHEADER')"
      >
        <div class="whatsapp-settings--content">
          <woot-input
            v-model.trim="infobipWidgetId"
            type="text"
            class="input"
            :placeholder="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_WIDGET_ID_PLACEHOLDER')"
          />
          <woot-button style="align-self: baseline; margin-left: 0.8rem;" @click="updateInfobipData">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>

      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_STATUS_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_STATUS_SUBHEADER')"
      >
        <div class="whatsapp-settings--content">
          <label class="input-container input">
            <select v-model="infobipStatus" style="margin-bottom: 0;">
              <option value="">{{ $t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_STATUS_ALL') }}</option>
              <option value="SOLVED">{{ $t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_STATUS_SOLVED') }}</option>
              <option value="CLOSED">{{ $t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_STATUS_CLOSED') }}</option>
              <option value="OPEN">{{ $t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_STATUS_OPEN') }}</option>
              <option value="WAITING">{{ $t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_STATUS_WAITING') }}</option>
            </select>
          </label>
          <woot-button style="align-self: baseline; margin-left: 0.8rem;" @click="updateInfobipData">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>

      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_PRIORITY_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_PRIORITY_SUBHEADER')"
      >
        <div class="whatsapp-settings--content">
          <label class="input-container input">
            <select v-model="infobipPriority" style="margin-bottom: 0;">
              <option value="">{{ $t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_PRIORITY_ALL') }}</option>
              <option value="URGENT">{{ $t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_PRIORITY_URGENT') }}</option>
              <option value="HIGH">{{ $t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_PRIORITY_HIGH') }}</option>
              <option value="NORMAL">{{ $t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_PRIORITY_NORMAL') }}</option>
              <option value="LOW">{{ $t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_PRIORITY_LOW') }}</option>
            </select>
          </label>
          <woot-button style="align-self: baseline; margin-left: 0.8rem;" @click="updateInfobipData">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>

      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_AGENT_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_AGENT_SUBHEADER')"
      >
        <div class="whatsapp-settings--content">
          <multiselect
            v-model="infobipAgents"
            select-label=""
            selected-label="Selecionado"
            deselect-label=""
            :placeholder="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_AGENT_PLACEHOLDER')"
            label="displayName"
            track-by="id"
            :loading="infobipLoadingAgents"
            :max-height="160"
            :allow-empty="true"
            :multiple="true"
            :options="infobipAgentsOptions"
            :disabled="infobipLoadingAgents"
            :searchable="true"
          >
            <span slot="noOptions">{{ $t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_AGENT_NO_OPTIONS') }}</span>
          </multiselect>
          <woot-button style="align-self: baseline; margin-left: 0.8rem;" @click="updateInfobipData">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>

      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_QUEUES_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_QUEUES_SUBHEADER')"
      >
        <div class="whatsapp-settings--content">
          <multiselect
            v-model="infobipQueues"
            selectedLabel="Selecionado"
            :placeholder="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_QUEUES_PLACEHOLDER')"
            label="name"
            track-by="id"
            deselect-label=""
            :loading="infobipLoadingQueues"
            :max-height="160"
            :allow-empty="true"
            :option-height="104"
            :multiple="true"
            :options="infobipQueuesOptions"
            :disabled="infobipLoadingQueues"
            :searchable="true"
          >
            <span slot="noOptions">{{ $t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_QUEUES_NO_OPTIONS') }}</span>
          </multiselect>
          <woot-button style="align-self: baseline; margin-left: 0.8rem;" @click="updateInfobipData">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>

      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_TAGS_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_TAGS_SUBHEADER')"
      >
        <div class="whatsapp-settings--content">
          <multiselect
            v-model="infobipTags"
            selectedLabel="Selecionado"
            :placeholder="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_TAGS_PLACEHOLDER')"
            label="name"
            track-by="name"
            deselect-label=""
            :loading="infobipLoadingTags"
            :max-height="160"
            :allow-empty="true"
            :option-height="104"
            :multiple="true"
            :options="infobipTagsOptions"
            :disabled="infobipLoadingTags"
            :searchable="true"
          >
            <span slot="noOptions">{{ $t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_TAGS_NO_OPTIONS') }}</span>
          </multiselect>
          <woot-button style="align-self: baseline; margin-left: 0.8rem;" @click="updateInfobipData">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>

      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_MIN_MESSAGES_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_MIN_MESSAGES_SUBHEADER')"
      >
        <div class="whatsapp-settings--content">
          <woot-input v-model.trim="infobipMinMessages" type="text" class="input" />
          <woot-button :disabled="$v.infobipMinMessages.$invalid" style="align-self: baseline; margin-left: 0.8rem;" @click="updateInfobipData">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>

      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_CUSTOMER_MIN_MESSAGES_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_CUSTOMER_MIN_MESSAGES_SUBHEADER')"
      >
        <div class="whatsapp-settings--content">
          <woot-input v-model.trim="infobipCustomerMinMessages" type="text" class="input" />
          <woot-button :disabled="$v.infobipCustomerMinMessages.$invalid" style="align-self: baseline; margin-left: 0.8rem;" @click="updateInfobipData">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>

      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_OPERATOR_MIN_MESSAGES_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_OPERATOR_MIN_MESSAGES_SUBHEADER')"
      >
        <div class="whatsapp-settings--content">
          <woot-input v-model.trim="infobipOperatorMinMessages" type="text" class="input" />
          <woot-button :disabled="$v.infobipOperatorMinMessages.$invalid" style="align-self: baseline; margin-left: 0.8rem;" @click="updateInfobipData">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>

      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_COUNT_BOT_MESSAGES_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_COUNT_BOT_MESSAGES_SUBHEADER')"
      >
        <div class="whatsapp-settings--content">
          <woot-switch v-model="infobipCountBotMessages" />
          <woot-button style="align-self: baseline; margin-left: 0.8rem;" @click="updateInfobipData">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>

      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_AGENT_CONVERSATION_LIMIT_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_AGENT_CONVERSATION_LIMIT_SUBHEADER')"
      >
        <div class="whatsapp-settings--content">
          <woot-input
            v-model.trim="infobipAgentConversationLimit"
            type="text"
            class="input"
            :placeholder="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_AGENT_CONVERSATION_LIMIT_PLACEHOLDER')"
          />
          <woot-button
            :disabled="$v.infobipAgentConversationLimit.$invalid"
            style="align-self: baseline; margin-left: 0.8rem;"
            @click="updateInfobipData"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>

      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_REPROCESS_TITLE')"
        :sub-title="
          $t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_REPROCESS_SUBHEADER')
        "
      >
        <div
          class="whatsapp-settings--content"
          style="flex-wrap: wrap; gap: 0.8rem;"
        >
          <woot-input
            v-model="infobipFetchBeginDate"
            type="date"
            class="input"
            :label="
              $t(
                'INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_REPROCESS_BEGIN_DATE_LABEL'
              )
            "
          />
          <woot-input
            v-model="infobipFetchEndDate"
            type="date"
            class="input"
            :label="
              $t(
                'INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_REPROCESS_END_DATE_LABEL'
              )
            "
          />
        </div>
        <div class="whatsapp-settings--content">
          <div class="enter-to-send--checkbox">
            <input
              id="infobipForce"
              v-model="infobipFetchForce"
              type="checkbox"
            />
            <label for="infobipForce">
              {{
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_REPROCESS_FORCE_LABEL'
                )
              }}
            </label>
          </div>
          <div v-if="infobipFetchForce" class="enter-to-send--checkbox">
            <input
              id="infobipUpdateExisting"
              v-model="infobipFetchUpdateExisting"
              type="checkbox"
            />
            <label for="infobipUpdateExisting">
              {{
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_REPROCESS_UPDATE_EXISTING_LABEL'
                )
              }}
            </label>
          </div>
          <woot-button
            :is-loading="infobipFetching"
            :disabled="
              !infobipFetchBeginDate || !infobipFetchEndDate || infobipFetching
            "
            @click="runInfobipFetch"
          >
            {{
              $t('INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_REPROCESS_BUTTON')
            }}
          </woot-button>
        </div>
      </settings-section>

    </div>
  </div>

  <div v-else-if="isGupshupWhatsAppChannel">
    <div v-if="inbox.provider_config" class="settings--content">
      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_APP_ID_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_APP_ID_SUBHEADER')"
      >
        <woot-code :script="inbox.provider_config.app_id" />
      </settings-section>
      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_SUBHEADER')"
      >
        <woot-code :script="inbox.provider_config.api_key" />
      </settings-section>
      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_TITLE')"
        :sub-title="
          $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_SUBHEADER')
        "
      >
        <div class="whatsapp-settings--content">
          <woot-input
            v-model.trim="gupshupInboxAPIKey"
            type="text"
            class="input"
            :placeholder="
              $t(
                'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_PLACEHOLDER'
              )
            "
          />
          <woot-button
            :disabled="$v.gupshupInboxAPIKey.$invalid"
            @click="updateGupshupInboxAPIKey"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
      <whatsapp-coex-settings :inbox="inbox" />
    </div>
  </div>

  <div v-else-if="isMultimediaChannel">
    <div class="settings--content">
      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.MULTIMEDIA_SECTION_WEBHOOK_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.MULTIMEDIA_SECTION_WEBHOOK_SUBHEADER')"
      >
        <woot-code :script="inbox.callback_webhook_url" />
      </settings-section>
      <settings-section
        v-if="isMidiaMultimedia || isMidiaExport"
        :title="
          $t('INBOX_MGMT.SETTINGS_POPUP.MULTIMEDIA_SECTION_STORAGE_ORIGIN_TITLE')
        "
        :sub-title="
          $t(
            'INBOX_MGMT.SETTINGS_POPUP.MULTIMEDIA_SECTION_STORAGE_ORIGIN_SUBHEADER'
          )
        "
      >
        <div class="whatsapp-settings--content">
          <label class="input-container input">
            <select
              v-model="multimediaOrigin"
              style="margin-bottom: 0;"
              @change="onMultimediaOriginChange"
            >
              <option
                v-for="opt in storageOrigins"
                :key="opt.value"
                :value="opt.value"
              >
                {{ $t(opt.labelKey) }}
              </option>
            </select>
          </label>
          <woot-button
            :disabled="
              $v.multimediaBucket.$invalid ||
              $v.multimediaCredentialsForValidation.$invalid
            "
            @click="updateMultimediaData"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
      <settings-section
        v-if="(isMidiaMultimedia || isMidiaExport) && currentOriginCredentialConfig"
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.' + currentOriginCredentialConfig.titleKey)"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.' + currentOriginCredentialConfig.subtitleKey)"
      >
        <!-- campos individuais (ex: Amazon S3, SFTP) -->
        <template v-if="currentOriginCredentialConfig.type === 'fields'">
          <p v-if="multimediaCredentialsLoading" class="help-text" style="margin-bottom: 8px;">
            {{ $t('INBOX_MGMT.ADD.MULTIMEDIA_CHANNEL.LOADING_CREDENTIALS') }}
          </p>
          <div
            v-for="(field, idx) in currentOriginCredentialConfig.fields"
            :key="field.key"
            class="whatsapp-settings--content"
            :style="idx < currentOriginCredentialConfig.fields.length - 1 ? 'margin-bottom: 8px;' : ''"
          >
            <woot-input
              v-model.trim="multimediaCredentialsByOrigin[multimediaOrigin][field.key]"
              :type="field.inputType"
              class="input"
              :placeholder="$t(field.placeholderKey)"
              :disabled="multimediaCredentialsLoading"
            />
            <woot-button
              v-if="idx === currentOriginCredentialConfig.fields.length - 1"
              :disabled="
                multimediaCredentialsLoading ||
                $v.multimediaBucket.$invalid ||
                $v.multimediaCredentialsForValidation.$invalid
              "
              @click="updateMultimediaData"
            >
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
            </woot-button>
          </div>
        </template>

        <!-- arquivo JSON (ex: GCS externo) -->
        <template v-else-if="currentOriginCredentialConfig.type === 'file'">
          <div class="whatsapp-settings--content">
            <label class="input-container input">
              <input
                ref="multimediaCredentialsInput"
                type="file"
                accept="application/json,.json"
                style="margin-bottom: 0;"
                @change="onMultimediaCredentialsFileChange"
              />
            </label>
            <woot-button
              :disabled="
                $v.multimediaBucket.$invalid ||
                $v.multimediaCredentialsForValidation.$invalid
              "
              @click="updateMultimediaData"
            >
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
            </woot-button>
          </div>
          <p
            v-if="multimediaCredentialsByOrigin[multimediaOrigin].credentials_filename"
            class="help-text"
          >
            {{ $t('INBOX_MGMT.ADD.MULTIMEDIA_CHANNEL.CREDENTIALS_FILE.LOADED') }}:
            {{ multimediaCredentialsByOrigin[multimediaOrigin].credentials_filename }}
          </p>
        </template>
      </settings-section>
      <settings-section
        v-if="(isMidiaMultimedia || isMidiaExport) && currentOriginUsesBucket"
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.MULTIMEDIA_SECTION_BUCKET_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.MULTIMEDIA_SECTION_BUCKET_SUBHEADER')"
      >
        <div class="whatsapp-settings--content">
          <woot-input
            v-model.trim="multimediaBucket"
            type="text"
            class="input"
            :placeholder="
              $t('INBOX_MGMT.SETTINGS_POPUP.MULTIMEDIA_SECTION_BUCKET_TITLE')
            "
          />
          <woot-button
            :disabled="
              $v.multimediaBucket.$invalid ||
              $v.multimediaCredentialsForValidation.$invalid
            "
            @click="updateMultimediaData"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
      <settings-section
        v-if="isMidiaExport || isMidiaMultimedia"
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.MULTIMEDIA_SECTION_PREFIX_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.MULTIMEDIA_SECTION_PREFIX_SUBHEADER')"
      >
        <div class="whatsapp-settings--content" >
          <woot-input
            v-model.trim="multimediaPrefix"
            type="text"
            class="input"

            :placeholder="
              $t('INBOX_MGMT.SETTINGS_POPUP.MULTIMEDIA_SECTION_PREFIX_TITLE')
            "
          />
          <woot-button
            :disabled="
              $v.multimediaBucket.$invalid ||
              $v.multimediaCredentialsForValidation.$invalid
            "
            @click="updateMultimediaData"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
    </div>
  </div>

  <div v-else-if="isOmneChannel">
    <div class="settings--content">
      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.OMNE_SECTION_WEBHOOK_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.OMNE_SECTION_WEBHOOK_SUBHEADER')"
      >
        <woot-code :script="inbox.callback_webhook_url" />
      </settings-section>
      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.OMNE_SECTION_USERNAME_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.OMNE_SECTION_USERNAME_SUBHEADER')"
      >
        <woot-code :script="inbox.username" />
      </settings-section>
      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.OMNE_SECTION_USERNAME_UPDATE_TITLE')"
        :sub-title="
          $t('INBOX_MGMT.SETTINGS_POPUP.OMNE_SECTION_USERNAME_UPDATE_SUBHEADER')
        "
      >
        <div class="whatsapp-settings--content">
          <woot-input
            v-model.trim="omneUsername"
            type="text"
            class="input"
            placeholder="Digite o novo username aqui"
          />
          <woot-button
            :disabled="$v.omneUsername.$invalid"
            @click="updateOmneUsername"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
      <settings-section
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.OMNE_SECTION_PASSWORD_UPDATE_TITLE')"
        :sub-title="
          $t('INBOX_MGMT.SETTINGS_POPUP.OMNE_SECTION_PASSWORD_UPDATE_SUBHEADER')
        "
      >
        <div class="whatsapp-settings--content">
          <woot-input
            v-model.trim="omnePassword"
            type="text"
            class="input"
            placeholder="Digite a nova senha aqui"
          />
          <woot-button
            :disabled="$v.omnePassword.$invalid"
            @click="updateOmnePassword"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </settings-section>
    </div>
  </div>

  <div v-else-if="isAWhatsAppChannel">
    <div class="settings--content">
      <whatsapp-coex-settings :inbox="inbox" />
    </div>
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import inboxMixin from 'shared/mixins/inboxMixin';
import multimediaMixin, {
  ORIGIN_CREDENTIAL_CONFIGS,
  STORAGE_ORIGINS,
} from 'shared/mixins/multimediaMixin';
import SettingsSection from '../../../../../components/SettingsSection';
import ImapSettings from '../ImapSettings';
import SmtpSettings from '../SmtpSettings';
import MicrosoftReauthorize from '../channels/microsoft/Reauthorize';
import { required, numeric, requiredIf } from 'vuelidate/lib/validators';
import { isPhoneE164OrEmpty } from 'shared/helpers/Validators';
import { update } from 'shared/helpers/vuex/mutationHelpers';
import WootButton from '../../../../../components/ui/WootButton.vue';
import WhatsappCoexSettings from './components/WhatsappCoexSettings.vue';

export default {
  components: {
    WootButton,
    SettingsSection,
    ImapSettings,
    SmtpSettings,
    MicrosoftReauthorize,
    WhatsappCoexSettings,
  },
  mixins: [inboxMixin, alertMixin, multimediaMixin],
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      hmacMandatory: false,
      handoffDefault: false,
      handoffMessageType: 'default_with_history',
      handoffMessage: '',
      showAgents: false,
      showCampaigns: false,
      showTabulation: false,
      showIgnoredUsers: false,
      showGroups: false,
      showTags: false,
      showUsers: false,
      whatsAppInboxAPIKey: '',
      audioWhatsAppInbox: null,
      gupshupInboxAPIKey: '',

      vollInboxAPIKey: '',
      vollNumber: this.inbox.custom_config?.number,
      vollAgents: this.inbox.custom_config?.agents,
      vollCampaigns: this.inbox.custom_config?.campaigns,
      vollTabulations: this.inbox.custom_config?.tabulations,
      vollMinMessages: this.inbox.custom_config?.min_messages,
      vollAgentsOptions: [],
      vollCampaingsOptions: [],
      vollTabulationsOptions: [],

      storageOrigins: STORAGE_ORIGINS,

      multimediaMidia: this.inbox?.Midia,
      multimediaBucket: this.inbox.custom_config?.bucket,
      multimediaPrefix: this.inbox.custom_config?.prefix,
      multimediaOrigin: this.inbox.origin || 'gcs_intelia',
      multimediaCredentialsByOrigin: Object.fromEntries(
        Object.entries(ORIGIN_CREDENTIAL_CONFIGS)
          .filter(([, cfg]) => cfg !== null)
          .map(([key, cfg]) => [key, cfg.initialState(this.inbox)])
      ),
      multimediaCredentialsLoading: false,

      wetalkieDomain: '',
      wetalkieUsername: '',
      wetalkiePassword: '',
      wetalkieTabulations: this.inbox.custom_config?.tabulations,
      wetalkieIgnoredUsers: this.inbox.custom_config?.ignored_users,
      wetalkieUsers: this.inbox.custom_config?.users,
      wetalkieMinMessages: this.inbox.custom_config?.min_messages,
      wetalkieCustomerMinMessages:
        this.inbox.custom_config?.customer_min_messages,
      wetalkieOperatorMinMessages:
        this.inbox.custom_config?.operator_min_messages,
      wetalkieCountBotMessages: this.inbox.custom_config?.count_bot_messages,
      wetalkieTabulationsOptions: [],
      wetalkieUserOptions: [],
      wetalkieLoadingTabulations: false,
      wetalkieLoadingUsers: false,
      wetalkieFetchBeginDate: '',
      wetalkieFetchEndDate: '',
      wetalkieFetchForce: false,
      wetalkieFetching: false,
      ingestionPausedUntil: this.inbox.ingestion_paused_until || null,
      ingestionPauseUntilInput: '',
      ingestionPauseLoading: false,

      infobipFetchBeginDate: '',
      infobipFetchEndDate: '',
      infobipFetchForce: false,
      infobipFetchUpdateExisting: false,
      infobipFetching: false,

      zendeskSubdomain : '',
      zendeskEmail: '',
      zendeskApiToken: '',
      zendeskChannels: this.inbox.custom_config?.channels,
      zendeskGroups: this.inbox.custom_config?.groups,
      zendeskTags: this.inbox.custom_config?.excluded_tags,
      zendeskUsers: this.inbox.custom_config?.users,
      zendeskExcludedUsers: this.inbox.custom_config?.excluded_users,
      zendeskMinMessages: this.inbox.custom_config?.min_messages,
      zendeskCustomerMinMessages: this.inbox.custom_config?.customer_min_messages,
      zendeskOperatorMinMessages: this.inbox.custom_config?.operator_min_messages,
      zendeskCountBotMessages: this.inbox.custom_config?.count_bot_messages,
      zendeskFetchBeginDate: '',
      zendeskFetchEndDate: '',
      zendeskFetchForce: false,
      zendeskFetching: false,
      zendeskChannelsOptions: [
        { id: 'chat', name: 'Chat' },
        { id: 'whatsapp', name: 'WhatsApp' },
        { id: 'native_messaging', name: 'Native Messaging' },
      ],
      zendeskGroupsOptions: [],
      zendeskTagsOptions: [],
      zendeskUsersOptions: [],
      zendeskLoadingGroups: false,
      zendeskLoadingTags: false,
      zendeskLoadingUsers: false,

      omneUsername: '',
      omnePassword: '',

      infobipApiKey: '',
      infobipBaseUrl: '',
      infobipStatus: this.inbox.custom_config?.status || '',
      infobipPriority: this.inbox.custom_config?.priority || '',
      infobipAgents: (this.inbox.custom_config?.agentIds || []).map(id => ({ id, displayName: id })),
      infobipQueues: (this.inbox.custom_config?.queueIds || []).map(id => ({ id, name: id })),
      infobipMinMessages: this.inbox.custom_config?.min_messages || '',
      infobipCustomerMinMessages: this.inbox.custom_config?.customer_min_messages || '',
      infobipOperatorMinMessages: this.inbox.custom_config?.operator_min_messages || '',
      infobipCountBotMessages: this.inbox.custom_config?.count_bot_messages || false,
      infobipAgentConversationLimit: this.inbox.custom_config?.agent_conversation_limit || '',
      infobipTags: (this.inbox.custom_config?.tagNames || []).map(name => ({ name })),
      infobipPhoneNumber: this.inbox.custom_config?.phone_number || '',
      infobipWidgetId: this.inbox.custom_config?.widget_id || '',
      infobipAgentsOptions: [],
      infobipQueuesOptions: [],
      infobipTagsOptions: [],
      infobipLoadingAgents: false,
      infobipLoadingQueues: false,
      infobipLoadingTags: false,
    };
  },
  validations: {
    handoffMessage: {
      required: requiredIf(vm => vm.handoffMessageType === 'custom'),
    },
    whatsAppInboxAPIKey: { required },
    gupshupInboxAPIKey: { required },
    vollInboxAPIKey: { required },
    wetalkieDomain: { required },
    wetalkieUsername: { required },
    wetalkiePassword: { required },
    vollNumber: { required, isPhoneE164OrEmpty },
    vollMinMessages: { required, numeric },
    wetalkieMinMessages: { required, numeric },
    wetalkieCustomerMinMessages: { required, numeric },
    wetalkieOperatorMinMessages: { required, numeric },
    omneUsername: { required },
    omnePassword: { required },
    infobipApiKey: { required },
    infobipBaseUrl: { required },
    infobipMinMessages: { numeric },
    infobipCustomerMinMessages: { numeric },
    infobipOperatorMinMessages: { numeric },
    infobipAgentConversationLimit: { numeric },
    zendeskSubdomain: { required },
    zendeskEmail: { required },
    zendeskApiToken: { required },
    zendeskMinMessages: { required, numeric },
    zendeskCustomerMinMessages: { required, numeric },
    zendeskOperatorMinMessages: { required, numeric },
    audioWhatsAppInbox: {
      required: requiredIf(function () {
        return this.is360DialogWhatsAppChannel;
      }),
    },
    multimediaBucket: {
      required: requiredIf(function () {
        return this.currentOriginUsesBucket;
      }),
    },
    multimediaCredentialsForValidation: {
      required: requiredIf(function () {
        return !!this.currentOriginCredentialConfig;
      }),
    },
  },
  watch: {
    inbox() {
      this.setDefaults();
      if (this.isInfobipChannel) {
        this.getInfobipData();
      }
    },
    infobipFetchForce(value) {
      if (!value) this.infobipFetchUpdateExisting = false;
    },
  },
  computed: {
    webchatLinkTag() {
      return `{% webchat_link ${this.inbox.id} %}`;
    },
    handoffMessageTypes() {
      return ['custom', 'default_only', 'default_with_history'].map(value => ({
        value,
        label: this.$t(
          `INBOX_MGMT.SETTINGS_POPUP.WEB_WIDGET_HANDOFF_MESSAGE_TYPES.${value.toUpperCase()}`
        ),
      }));
    },
    getwhatsAppAudios() {
      return [
        { key: 1, text: 'Padrão', value: '/opt/janus/welcome.opus', type: 'filename', mimetype: 'audio/x-opus+ogg' },
      ];
    },
    currentOriginCredentialConfig() {
      return ORIGIN_CREDENTIAL_CONFIGS[this.multimediaOrigin] || null;
    },
    currentOriginUsesBucket() {
      const config = ORIGIN_CREDENTIAL_CONFIGS[this.multimediaOrigin];
      return !config || config.usesBucket !== false;
    },
    infobipChannelTypeLabel() {
      const types = { whatsapp: 'WhatsApp', live_chat: 'Live Chat' };
      return types[this.inbox.infobip_channel_type] || this.inbox.infobip_channel_type || '-';
    },
    multimediaCredentialsForValidation() {
      const config = this.currentOriginCredentialConfig;
      if (!config) return true;

      const creds = this.multimediaCredentialsByOrigin[this.multimediaOrigin];

      if (config.type === 'fields') {
        const allFilled = config.fields.every(f => creds?.[f.key]?.trim());
        return allFilled ? { filled: true } : null;
      }

      if (config.type === 'file') {
        if (creds?.credentials && typeof creds.credentials === 'object') {
          return creds.credentials;
        }
        if (creds?.credentials_filename?.trim()) return { hasFilename: true };
        return null;
      }

      return true;
    },
    isIngestionPaused() {
      if (!this.ingestionPausedUntil) return false;
      const until = new Date(this.ingestionPausedUntil);
      return !Number.isNaN(until.getTime()) && until.getTime() > Date.now();
    },
    ingestionPausedLabel() {
      if (!this.ingestionPausedUntil) return '';
      const until = new Date(this.ingestionPausedUntil);
      if (Number.isNaN(until.getTime())) return '';
      return until.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
  mounted() {
    this.setDefaults();
    if (this.isVollChannel) {
      this.getVollData();
    }
    if (this.isWetalkieChannel) {
      this.getWetalkieData();
    }
    if (this.isZendeskChannel) {
      this.getZendeskData();
    }
    if (this.isMultimediaChannel) {
      this.loadMultimediaCredentials(this.multimediaOrigin);
    }
    if (this.isInfobipChannel) {
      this.getInfobipData();
    }
  },
  methods: {
    update,
    getVollData() {
      this.getAgentsVoll360();
      this.getCampaignsVoll360();
      this.getTabulationsVoll360();
    },
    getWetalkieData() {
      this.getTabulationsWetalkie();
      this.getUsersWetalkie();
    },
    getZendeskData() {
      if (this.inbox.email && this.inbox.api_token && this.inbox.subdomain) {
        this.getGroupsZendesk();
        this.getTagsZendesk();
        this.getUsersZendesk();
      }
    },
    async getUsersZendesk() {
      try {
        this.zendeskLoadingUsers = true;

        const response = await this.$store.dispatch(
          'inboxes/getZendeskUsers',
          {
            subdomain: this.inbox.subdomain,
            email: this.inbox.email,
            api_token: this.inbox.api_token,
          }
        );
        if (response) {
          this.zendeskUsersOptions = response.data.items;
          this.showUsers = true;
        }
      } catch (error) {
        this.showAlert(
          this.$t('INBOX_MGMT.ADD.ZENDESK_CHANNEL.API.ERROR_MESSAGE')
        );
      }

      this.zendeskLoadingUsers = false;
    },
    async getGroupsZendesk() {
      try {
        this.zendeskLoadingGroups = true;

        const response = await this.$store.dispatch(
          'inboxes/getZendeskGroups',
          {
            subdomain: this.inbox.subdomain,
            email: this.inbox.email,
            api_token: this.inbox.api_token,
          }
        );
        if (response) {
          this.zendeskGroupsOptions = response.data.items;
          this.showGroups = true;
        }
      } catch (error) {
        this.showAlert(
          this.$t('INBOX_MGMT.ADD.ZENDESK_CHANNEL.API.ERROR_MESSAGE')
        );
      }

      this.zendeskLoadingGroups = false;
    },
    async getTagsZendesk() {
      try {
        this.zendeskLoadingTags = true;

        const response = await this.$store.dispatch(
          'inboxes/getZendeskTags',
          {
            subdomain: this.inbox.subdomain,
            email: this.inbox.email,
            api_token: this.inbox.api_token,
          }
        );
        if (response) {
          this.zendeskTagsOptions = response.data.items;
          this.showTags = true;
        }
      } catch (error) {
        this.showAlert(
          this.$t('INBOX_MGMT.ADD.ZENDESK_CHANNEL.API.ERROR_MESSAGE')
        );
      }

      this.zendeskLoadingTags = false;
    },
    async getAgentsVoll360() {
      if (this.inbox.api_key && this.inbox.domain) {
        try {
          const response = await this.$store.dispatch(
            'inboxes/getVollAgents',
            {
              domain: this.inbox.domain,
              api_key: this.inbox.api_key,
            });

          if (response) {
            this.vollAgentsOptions = response;
            this.showAgents = true;
          }
        } catch (error) {
          this.showAlert(this.$t('INBOX_MGMT.ADD.VOLL360_CHANNEL.API.ERROR_MESSAGE'));
        }
      }
    },
    async getCampaignsVoll360() {
      if (this.inbox.api_key && this.inbox.domain) {
        try {
          const response = await this.$store.dispatch(
            'inboxes/getVollCampaigns',
            {
              domain: this.inbox.domain,
              api_key: this.inbox.api_key,
            });

          if (response) {
            this.vollCampaingsOptions = response;
            this.showCampaigns = true;
          }
        } catch (error) {
          this.showAlert(this.$t('INBOX_MGMT.ADD.VOLL360_CHANNEL.API.ERROR_MESSAGE'));
        }
      }
    },
    async getTabulationsVoll360() {
      if (this.inbox.api_key && this.inbox.domain) {
        try {
          const response = await this.$store.dispatch(
            'inboxes/getVollTabulations',
            {
              domain: this.inbox.domain,
              api_key: this.inbox.api_key,
            });

          if (response) {
            this.vollTabulationsOptions = response;
            this.showTabulation = true;
          }
        } catch (error) {
          this.showAlert(this.$t('INBOX_MGMT.ADD.VOLL360_CHANNEL.API.ERROR_MESSAGE'));
        }
      }
    },
    async getTabulationsWetalkie() {
      if (this.inbox.username && this.inbox.domain && this.inbox.identifier) {
        try {
          this.wetalkieLoadingTabulations = true;
          this.wetalkieTabulationsOptions = [];
          const response = await this.$store.dispatch(
            'inboxes/getWetalkieTabulationsByInbox',
            {
              identifier: this.inbox.identifier
            });

          if (response) {
            this.wetalkieTabulationsOptions = response.data.items;
            this.showTabulation = true;
          }
        } catch (error) {
          this.showAlert(this.$t('INBOX_MGMT.ADD.VOLL360_CHANNEL.API.ERROR_MESSAGE'));
        }

        this.wetalkieLoadingTabulations = false;
      }
    },
    async getUsersWetalkie() {
      if (this.inbox.username && this.inbox.domain && this.inbox.identifier) {
        try {
          this.wetalkieLoadingUsers = true;

          const response = await this.$store.dispatch(
            'inboxes/getWetalkieUsersByInbox',
            {
              identifier: this.inbox.identifier
            }
          );
          if (response) {
            this.wetalkieUserOptions = response.data.items;
            this.showIgnoredUsers = true;
          }
        } catch (error) {
          this.showAlert(this.$t('INBOX_MGMT.ADD.WETALKIE_CHANNEL.API.ERROR_MESSAGE'));
        }
        this.wetalkieLoadingUsers = false;
      }
    },
    async runWetalkieFetch() {
      if (!this.wetalkieFetchBeginDate || !this.wetalkieFetchEndDate) return;
      try {
        this.wetalkieFetching = true;
        await this.$store.dispatch('inboxes/fetchWetalkieConversations', {
          identifier: this.inbox.identifier,
          beginDate: this.wetalkieFetchBeginDate,
          endDate: this.wetalkieFetchEndDate,
          force: this.wetalkieFetchForce,
        });
        this.showAlert(
          'Execução retroativa iniciada. As conversas serão processadas em segundo plano.'
        );
      } catch (error) {
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.wetalkieFetching = false;
      }
    },
    async runZendeskFetch() {
      if (!this.zendeskFetchBeginDate || !this.zendeskFetchEndDate) return;
      try {
        this.zendeskFetching = true;
        await this.$store.dispatch('inboxes/fetchZendeskConversations', {
          identifier: this.inbox.identifier,
          beginDate: this.zendeskFetchBeginDate,
          endDate: this.zendeskFetchEndDate,
          force: this.zendeskFetchForce,
        });
        this.showAlert(
          'Execução retroativa iniciada. As conversas serão processadas em segundo plano.'
        );
      } catch (error) {
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.zendeskFetching = false;
      }
    },
    async blockIngestion() {
      if (!this.ingestionPauseUntilInput) return;
      const until = new Date(this.ingestionPauseUntilInput);
      if (Number.isNaN(until.getTime())) {
        this.showAlert('Data/hora inválida.');
        return;
      }
      if (until.getTime() <= Date.now()) {
        this.showAlert('A data/hora precisa ser no futuro.');
        return;
      }
      try {
        this.ingestionPauseLoading = true;
        const data = await this.$store.dispatch('inboxes/pauseInboxIngestion', {
          inboxId: this.inbox.id,
          pauseUntil: until.toISOString(),
        });
        this.ingestionPausedUntil = data?.ingestion_paused_until || null;
        this.ingestionPauseUntilInput = '';
        this.showAlert('Ingestão de conversas bloqueada.');
      } catch (error) {
        this.showAlert(error.message || this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.ingestionPauseLoading = false;
      }
    },
    async releaseIngestion() {
      try {
        this.ingestionPauseLoading = true;
        await this.$store.dispatch('inboxes/pauseInboxIngestion', {
          inboxId: this.inbox.id,
          pauseUntil: null,
        });
        this.ingestionPausedUntil = null;
        this.showAlert('Ingestão de conversas liberada.');
      } catch (error) {
        this.showAlert(error.message || this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.ingestionPauseLoading = false;
      }
    },
    async runInfobipFetch() {
      if (!this.infobipFetchBeginDate || !this.infobipFetchEndDate) return;
      try {
        this.infobipFetching = true;
        await this.$store.dispatch('inboxes/fetchInfobipConversations', {
          identifier: this.inbox.identifier,
          beginDate: this.infobipFetchBeginDate,
          endDate: this.infobipFetchEndDate,
          force: this.infobipFetchForce,
          updateExisting: this.infobipFetchUpdateExisting,
        });
        this.showAlert(
          this.$t(
            'INBOX_MGMT.SETTINGS_POPUP.INFOBIP_SECTION_REPROCESS_SUCCESS_MESSAGE'
          )
        );
      } catch (error) {
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.infobipFetching = false;
      }
    },
    setDefaults() {
      this.hmacMandatory = this.inbox.hmac_mandatory || false;
      this.handoffDefault = this.inbox.handoff_default || false;
      this.handoffMessageType =
        this.inbox.handoff_message_type || 'default_with_history';
      this.handoffMessage = this.inbox.handoff_message || '';
      this.audioWhatsAppInbox = this.inbox?.provider_config?.audio?.key || 1; // Default audio
    },
    handleHmacFlag() {
      this.updateInbox();
    },
    async updateHandoffDefault() {
      try {
        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          formData: false,
          channel: { handoff_default: this.handoffDefault },
        });
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateHandoffMessage() {
      try {
        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          formData: false,
          channel: {
            handoff_message_type: this.handoffMessageType,
            handoff_message: this.handoffMessage,
          },
        });
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateInbox() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            hmac_mandatory: this.hmacMandatory,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateWhatsAppInboxAPIKey() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {},
        };

        payload.channel.provider_config = {
          ...this.inbox.provider_config,
          api_key: this.whatsAppInboxAPIKey,
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateAudioWhatsAppInbox() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {},
        };

        payload.channel.provider_config = {
          ...this.inbox.provider_config,
          audio: this.getwhatsAppAudios.find(audio => audio.key === this.audioWhatsAppInbox),
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateVollInboxAPIKey() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            api_key: this.vollInboxAPIKey
          },
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
        this.inbox.api_key = this.vollInboxAPIKey;

        this.getVollData();
        this.vollAgents = [];
        this.vollCampaigns = [];
        this.vollTabulations = [];

        await this.updateVollData();
      } catch (error) {
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateWetalkieUsername() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            username: this.wetalkieUsername
          },
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
        this.inbox.username = this.wetalkieUsername;

        this.getWetalkieData();
        this.wetalkieTabulations = [];
        this.wetalkieIgnoredUsers = [];
        this.wetalkieUsers = [];
        await this.updateWetalkieData();
      } catch (error) {
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateWetalkiePassword() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            password: this.wetalkiePassword
          },
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
        this.inbox.password = this.wetalkiePassword;

        this.getWetalkieData();
        this.wetalkieTabulations = [];
        this.wetalkieIgnoredUsers = [];
        this.wetalkieUsers = [];
        await this.updateWetalkieData();
      } catch (error) {
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateZendeskField(fieldName, value) {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            [fieldName]: value
          },
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
        this.inbox.subdomain = this.zendeskSubdomain;

        this.getZendeskData();
        this.zendeskGroups = [];
        this.zendeskUsers = [];
        this.zendeskExcludedUsers = [];
        this.zendeskTags = [];
        await this.updateWetalkieData();
      } catch (error) {
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateZendeskSubdomain() {
      await this.updateZendeskField('subdomain', this.zendeskSubdomain);
    },
    async updateZendeskEmail() {
      await this.updateZendeskField('email', this.zendeskEmail);
    },
    async updateZendeskApiToken() {
      await this.updateZendeskField('api_token', this.zendeskApiToken);
    },
    async updateWhatsAppInboxProvider() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            provider: '360_cloud',
          },
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateVollNumber() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {},
        };

        payload.channel.custom_config = {
          ...this.inbox.custom_config,
          number: this.vollNumber,
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateVollData() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {},
        };

        this.inbox.custom_config.agents = this.vollAgents;
        this.inbox.custom_config.campaigns = this.vollCampaigns;
        this.inbox.custom_config.tabulations = this.vollTabulations;

        payload.channel.custom_config = this.inbox.custom_config;

        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateWetalkieData() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {},
        };

        this.inbox.custom_config.tabulations = this.wetalkieTabulations;
        this.inbox.custom_config.ignored_users = this.wetalkieIgnoredUsers;
        this.inbox.custom_config.users = this.wetalkieUsers;
        this.inbox.custom_config.min_messages = parseInt(
          this.wetalkieMinMessages
        );
        this.inbox.custom_config.customer_min_messages = parseInt(
          this.wetalkieCustomerMinMessages
        );
        this.inbox.custom_config.operator_min_messages = parseInt(
          this.wetalkieOperatorMinMessages
        );
        this.inbox.custom_config.count_bot_messages =
          this.wetalkieCountBotMessages;
        payload.channel.custom_config = this.inbox.custom_config;

        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateZendeskData() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {},
        };

        this.inbox.custom_config.channels = this.zendeskChannels.map(obj => ({ id: obj.id, name: obj.name }))
        this.inbox.custom_config.groups = this.zendeskGroups.map(obj => ({ id: obj.id, name: obj.name }))
        this.inbox.custom_config.users = this.zendeskUsers.map(obj => ({ id: obj.id, name: obj.name, email: obj.email }))
        this.inbox.custom_config.excluded_users = this.zendeskExcludedUsers.map(obj => ({ id: obj.id, name: obj.name, email: obj.email }))
        this.inbox.custom_config.excluded_tags = this.zendeskTags.map(obj => ({ count: obj.count, name: obj.name }))
        this.inbox.custom_config.min_messages = parseInt(this.zendeskMinMessages);
        this.inbox.custom_config.customer_min_messages = parseInt(this.zendeskCustomerMinMessages);
        this.inbox.custom_config.operator_min_messages = parseInt(this.zendeskOperatorMinMessages);
        this.inbox.custom_config.count_bot_messages = this.zendeskCountBotMessages;
        payload.channel.custom_config = this.inbox.custom_config;

        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateVollAgents() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {},
        };

        payload.channel.custom_config = {
          ...this.inbox.custom_config,
          agents: this.vollAgents,
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateVollCampaigns() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {},
        };

        payload.channel.custom_config = {
          ...this.inbox.custom_config,
          campaigns: this.vollCampaigns,
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateVollTabulations() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {},
        };

        payload.channel.custom_config = {
          ...this.inbox.custom_config,
          tabulations: this.vollTabulations,
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateVollMinMessages() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {},
        };

        payload.channel.custom_config = {
          ...this.inbox.custom_config,
          min_messages: parseInt(this.vollMinMessages),
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateGupshupInboxAPIKey() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {},
        };

        payload.channel.provider_config = {
          ...this.inbox.provider_config,
          api_key: this.gupshupInboxAPIKey,
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateOmneUsername() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            username: this.omneUsername
          },
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
        this.inbox.username = this.omneUsername;

      } catch (error) {
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateOmnePassword() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            password: this.omnePassword
          },
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
        this.omnePassword = '';

      } catch (error) {
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    buildMultimediaCustomConfigForSubmit() {
      const originConfig = ORIGIN_CREDENTIAL_CONFIGS[this.multimediaOrigin];
      const usesBucket = !originConfig || originConfig.usesBucket !== false;

      const config = {
        bucket: usesBucket ? (this.multimediaBucket || '') : '',
        prefix: this.multimediaPrefix || '',
      };
      if (!originConfig) return config;

      const creds = this.multimediaCredentialsByOrigin[this.multimediaOrigin];

      if (originConfig.type === 'fields') {
        config.credentials = Object.fromEntries(
          originConfig.fields.map(f => {
            const val = creds?.[f.key];
            const out = f.key === 'port' ? (parseInt(val, 10) || 0) : (val || '');
            return [f.key, out];
          })
        );
      } else if (originConfig.type === 'file') {
        if (creds?.credentials && typeof creds.credentials === 'object' && !Array.isArray(creds.credentials)) {
          config.credentials = { ...creds.credentials };
        }
        if (creds?.credentials_filename) {
          config.credentials_filename = creds.credentials_filename;
        }
      }
      return config;
    },
    async updateMultimediaData() {
      this.$v.multimediaBucket.$touch();
      this.$v.multimediaCredentialsForValidation.$touch();
      if (
        this.$v.multimediaBucket.$invalid ||
        this.$v.multimediaCredentialsForValidation.$invalid
      ) {
        return;
      }

      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            origin: this.multimediaOrigin,
            custom_config: this.buildMultimediaCustomConfigForSubmit(),
          },
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        console.error(error);
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    onMultimediaOriginChange() {
      const input = this.$refs.multimediaCredentialsInput;
      if (input) input.value = '';
    },
    async loadMultimediaCredentials(origin) {
      const config = ORIGIN_CREDENTIAL_CONFIGS[origin];
      if (!config?.loadFromSecret || !this.inbox?.id) return;

      this.multimediaCredentialsLoading = true;

      const TIMEOUT_MS = 8000;
      const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('timeout')), TIMEOUT_MS)
      );

      try {
        const response = await Promise.race([
          this.$store.dispatch('inboxes/getMultimediaCredentials', {
            inboxId: this.inbox.id,
          }),
          timeout,
        ]);
        const creds = response?.credentials;
        if (creds && this.multimediaCredentialsByOrigin[origin]) {
          config.fields.forEach(f => {
            this.multimediaCredentialsByOrigin[origin][f.key] = creds[f.key] !== undefined
              ? String(creds[f.key])
              : '';
          });
        }
      } catch (_error) {
        // secret may not exist yet or request timed out — keep fields empty
      } finally {
        this.multimediaCredentialsLoading = false;
      }
    },
    getInfobipData() {
      this.getInfobipAgents();
      this.getInfobipQueues();
      this.getInfobipTags();
    },
    async getInfobipAgents() {
      try {
        this.infobipLoadingAgents = true;
        const response = await this.$store.dispatch('inboxes/getInfobipAgentsByInbox', {
          inboxId: this.inbox.id,
          params: {},
        });
        if (response?.data?.agents) {
          this.infobipAgentsOptions = response.data.agents;
          const savedIds = this.inbox.custom_config?.agentIds || [];
          if (savedIds.length) {
            this.infobipAgents = response.data.agents.filter(a => savedIds.includes(a.id));
          }
        }
      } catch (_) {
        // silently ignore if API credentials are not yet configured
      } finally {
        this.infobipLoadingAgents = false;
      }
    },
    async getInfobipQueues() {
      try {
        this.infobipLoadingQueues = true;
        const response = await this.$store.dispatch('inboxes/getInfobipQueuesByInbox', {
          inboxId: this.inbox.id,
          params: {},
        });
        if (response?.data?.queues) {
          this.infobipQueuesOptions = response.data.queues;
          const savedIds = this.inbox.custom_config?.queueIds || [];
          if (savedIds.length) {
            this.infobipQueues = response.data.queues.filter(q => savedIds.includes(q.id));
          }
        }
      } catch (_) {
        // silently ignore
      } finally {
        this.infobipLoadingQueues = false;
      }
    },
    async getInfobipTags() {
      try {
        this.infobipLoadingTags = true;
        const response = await this.$store.dispatch('inboxes/getInfobipTagsByInbox', {
          inboxId: this.inbox.id,
          params: {},
        });
        if (response?.data?.tags) {
          this.infobipTagsOptions = response.data.tags;
          const savedNames = this.inbox.custom_config?.tagNames || [];
          if (savedNames.length) {
            this.infobipTags = response.data.tags.filter(t => savedNames.includes(t.name));
          }
        }
      } catch (_) {
        // silently ignore
      } finally {
        this.infobipLoadingTags = false;
      }
    },
    async updateInfobipCredential(field, value) {
      try {
        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          formData: false,
          channel: { [field]: value },
        });
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
        this.getInfobipData();
      } catch (_) {
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateInfobipData() {
      try {
        const customConfig = { ...(this.inbox.custom_config || {}) };
        if (this.infobipStatus) customConfig.status = this.infobipStatus;
        else delete customConfig.status;

        if (this.infobipPriority) customConfig.priority = this.infobipPriority;
        else delete customConfig.priority;

        if (this.infobipAgents?.length) customConfig.agentIds = this.infobipAgents.map(a => a.id);
        else delete customConfig.agentIds;

        customConfig.queueIds = this.infobipQueues.map(q => q.id);

        if (this.infobipTags?.length) {
          customConfig.tagNames = this.infobipTags.map(t => t.name);
        } else {
          delete customConfig.tagNames;
        }

        if (this.infobipMinMessages) customConfig.min_messages = parseInt(this.infobipMinMessages);
        else delete customConfig.min_messages;

        if (this.infobipCustomerMinMessages) customConfig.customer_min_messages = parseInt(this.infobipCustomerMinMessages);
        else delete customConfig.customer_min_messages;

        if (this.infobipOperatorMinMessages) customConfig.operator_min_messages = parseInt(this.infobipOperatorMinMessages);
        else delete customConfig.operator_min_messages;

        customConfig.count_bot_messages = this.infobipCountBotMessages;

        if (this.infobipAgentConversationLimit) {
          customConfig.agent_conversation_limit = parseInt(this.infobipAgentConversationLimit, 10);
        } else {
          delete customConfig.agent_conversation_limit;
        }

        if (this.inbox.infobip_channel_type === 'whatsapp' && this.infobipPhoneNumber) {
          customConfig.phone_number = this.infobipPhoneNumber;
        } else {
          delete customConfig.phone_number;
        }

        if (this.inbox.infobip_channel_type === 'live_chat' && this.infobipWidgetId) {
          customConfig.widget_id = this.infobipWidgetId;
        } else {
          delete customConfig.widget_id;
        }

        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          formData: false,
          channel: { custom_config: customConfig },
        });
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (_) {
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    onMultimediaCredentialsFileChange(event) {
      const file = event?.target?.files?.[0];
      if (!file) return;
      const origin = this.multimediaOrigin;

      const reader = new FileReader();
      reader.onload = () => {
        try {
          const parsed = JSON.parse(reader.result);
          this.multimediaCredentialsByOrigin[origin].credentials = parsed;
          this.multimediaCredentialsByOrigin[origin].credentials_filename = file.name;
        } catch (e) {
          this.multimediaCredentialsByOrigin[origin].credentials = undefined;
          this.multimediaCredentialsByOrigin[origin].credentials_filename = file.name;
          this.showAlert(
            this.$t('INBOX_MGMT.ADD.MULTIMEDIA_CHANNEL.CREDENTIALS_FILE.INVALID_JSON')
          );
        }
      };
      reader.onerror = () => {
        this.showAlert(
          this.$t('INBOX_MGMT.ADD.MULTIMEDIA_CHANNEL.CREDENTIALS_FILE.READ_ERROR')
        );
      };
      reader.readAsText(file);
    },
  },
};
</script>
<style lang="scss" scoped>
.whatsapp-settings--content {
  align-items: center;
  display: flex;
  flex: 1;
  justify-content: space-between;
  margin-top: var(--space-small);

  .input {
    flex: 1;
    margin-right: var(--space-small);
    ::v-deep input {
      margin-bottom: 0;
    }
  }
}

.hmac-link-to-docs {
  margin-top: var(--space-small);
}

.ingestion-status {
  margin: 0 0 var(--space-small);
  font-weight: var(--font-weight-medium);

  &--paused {
    color: var(--r-600);
  }

  &--active {
    color: var(--g-600);
  }
}
</style>
